import { Box, Stack } from "@mui/material";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { TResult } from "../../hooks/useDocumentListWithData";
import {
  TDocument,
  THistoryDisplayResponse,
  TUserFullInfo,
  TVerificationRequest,
} from "../../models";
import { ActionPanel } from "../ActionPanel";
import { VerificationAppDocumentType } from "../../../app/models";
import {
  getRegistryEntry,
  DocumentTypeRenderer,
  UserInformationFallback,
} from "../StepAccordion";
import { DuplicatesPanel } from "../DuplicatesPanel";
import { LeftColumn, RightColumn } from "./styles";
import { DatabaseScreeningPanel } from "../DatabaseScreeningPanel";
import { HistoryPanel } from "../HistoryPanel";
import { IdentificationPanel } from "../IdentificationPanel";
import { StellarPanel } from "../StellarPanel";
import { LinkedVRsPanel } from "../LinkedVRsPanel";
import { useWidth } from "../../../app/hooks/useWidth";
import { useRegexList } from "../../../regexConfig/hooks/useRegexList";
import { useAuth } from "../../../../auth/useAuth";
import { VerificationStatus } from "../../utils/getStatus";
import { TVerificationStatusActionsResult } from "../../hooks/useVerificationStatusActions";
import { ICompanyVerificationStatusesConfig } from "../../network";
import { useOnboardingConfig } from "../../context/OnboardingConfigContext";
import { titleCase } from "../../../../utils";
import {
  effectiveStepDocumentType,
  matchQuestionnaireDocuments,
} from "../../utils/questionnaireDocumentMatching";
import {
  AddableDocumentCandidate,
  getAddableDocumentCandidates,
} from "../../utils/addableDocumentCandidates";
import { AddDocumentControl } from "../StepAccordion/AddDocumentControl";
import { ChecksPanel } from "../ChecksPanel";
import { useExternalProviderIDs } from "../../hooks/useExternalProviderIDs";
import {
  createPendingDocument,
  createPlaceholderDocument,
} from "../../utils/pendingDocument";
import { PageTabs } from "../../../../components/PageTabs";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useScreeningEvents } from "../../hooks/useScreeningEvents";
import { mainTheme } from "../../../../theme/mainTheme";

const EMPTY_USER_INFORMATION_DOCUMENT = createPlaceholderDocument(
  VerificationAppDocumentType.UserInformation,
);

export const PageVerificationContent: FC<{
  data: TVerificationRequest;
  statusesConfig: ICompanyVerificationStatusesConfig;
  documents: TResult;
  history: THistoryDisplayResponse | null;
  onAction: (value: string, to?: VerificationStatus) => void;
  id?: string;
  loading: boolean;
  comments: any;
  userInfo: TUserFullInfo;
  actions: TVerificationStatusActionsResult;
  addComment: (userDocumentId: string, comment: string) => void;
  canEdit: boolean;
  onRefresh?: () => Promise<void> | void;
  onLoadMoreHistory?: () => void;
}> = ({
  data,
  documents,
  history,
  onAction,
  id,
  loading,
  comments,
  addComment,
  canEdit,
  userInfo,
  actions,
  statusesConfig,
  onRefresh,
  onLoadMoreHistory,
}) => {
  const width = useWidth();
  const { companyRole } = useAuth();
  useRegexList(true);
  const { config } = useOnboardingConfig();
  const [externalIds, , , refreshExternalIds] = useExternalProviderIDs(data.id);
  const [screeningEvents, , , refreshScreeningEvents] = useScreeningEvents(data.id);
  const [activeTab, setActiveTab] = useState(0);
  const [pendingDocuments, setPendingDocuments] = useState<TDocument[]>([]);
  const [expandedDocumentType, setExpandedDocumentType] = useState<string>();

  const persistedDocumentTypes = useMemo(
    () =>
      new Set(documents.map((document) => document.document.documentType)),
    [documents],
  );

  const displayedDocuments = useMemo(
    () => [
      ...documents,
      ...pendingDocuments.filter(
        (document) =>
          !persistedDocumentTypes.has(document.document.documentType),
      ),
    ],
    [documents, pendingDocuments, persistedDocumentTypes],
  );

  useEffect(() => {
    setActiveTab(0);
  }, [data.id]);

  useEffect(() => {
    setPendingDocuments((current) => {
      const next = current.filter(
        (document) =>
          !persistedDocumentTypes.has(document.document.documentType),
      );
      return next.length === current.length ? current : next;
    });
  }, [persistedDocumentTypes]);

  const matchedQuestionnaireDocuments = useMemo(
    () => matchQuestionnaireDocuments(config?.elements, displayedDocuments),
    [config?.elements, displayedDocuments]
  );

  const stepDocuments = useMemo(
    () =>
      matchedQuestionnaireDocuments.stepDocuments.map((step) =>
        step.element.type === VerificationAppDocumentType.UserInformation &&
        step.documents.length === 0
          ? { ...step, documents: [EMPTY_USER_INFORMATION_DOCUMENT] }
          : step,
      ),
    [matchedQuestionnaireDocuments.stepDocuments],
  );
  const { orphanDocuments } = matchedQuestionnaireDocuments;

  const addableCandidates = useMemo(
    () =>
      getAddableDocumentCandidates(stepDocuments, canEdit, {
        identificationProvider: config?.identification_provider,
      }),
    [stepDocuments, canEdit, config?.identification_provider],
  );

  const handleAddDocument = useCallback(
    (candidate: AddableDocumentCandidate) => {
      setPendingDocuments((current) => {
        if (
          persistedDocumentTypes.has(candidate.documentType) ||
          current.some(
            (document) =>
              document.document.documentType === candidate.documentType,
          )
        ) {
          return current;
        }

        return [...current, createPendingDocument(candidate.documentType)];
      });
      setExpandedDocumentType(candidate.documentType);
    },
    [persistedDocumentTypes],
  );

  const hasUserInformationStep = useMemo(
    () =>
      stepDocuments.some(
        ({ element }) => element.type === VerificationAppDocumentType.UserInformation
      ),
    [stepDocuments]
  );
  const isIndividual = data.category === "individual";
  const hasUnacknowledgedScreeningEvent = screeningEvents?.some(
    (event) => !event.ackedBy,
  );
  const tabs = [
    { id: 0, label: "Applicants data" },
    {
      id: 1,
      label: (
        <Stack direction="row" alignItems="center" spacing={1}>
          <span>Case activity</span>
          {hasUnacknowledgedScreeningEvent && (
            <WarningAmberIcon
              aria-label="Screening events need attention"
              sx={{ color: mainTheme.palette.warning.main, fontSize: 20 }}
            />
          )}
        </Stack>
      ),
    },
  ];

  return (
    <>
      {["xs"].includes(width) ? (
        <RightColumn>
          <ActionPanel
            statusesConfig={statusesConfig}
            actions={actions}
            companyRole={companyRole}
            statusReason={data?.statusReason}
            riskLevel={
              data?.riskLevel
                ? {
                    riskLevel: data.riskLevel,
                    riskLevelReason: data?.riskLevelReason,
                  }
                : null
            }
            loading={loading}
            status={data?.status}
            onAction={onAction}
          />
          {isIndividual ? (
            <IdentificationPanel data={data} documents={documents} />
          ) : null}
          <DatabaseScreeningPanel
            data={data}
            onRefresh={onRefresh}
            onExternalIdsRefresh={refreshExternalIds}
          />
          <DuplicatesPanel />
          <StellarPanel data={data} />
          <LinkedVRsPanel verificationRequestId={id} statusesConfig={statusesConfig} />
        </RightColumn>
      ) : null}
      <Box sx={{ margin: "32px 0 24px" }}>
        <PageTabs
          tabs={tabs}
          value={activeTab}
          onChange={(_event, newValue) => setActiveTab(newValue)}
        />
      </Box>
      <Box width='100%' paddingBottom='40px'>
        <Stack
          direction='row'
          justifyContent='center'
          alignItems='flex-start'
          spacing={2}
        >
          <LeftColumn
            sx={{
              flexGrow: 2,
            }}
          >
            {activeTab === 0 ? (
              <>
                {!hasUserInformationStep && (
                  <UserInformationFallback
                    userInfo={userInfo}
                    isMobile={["xs", "sm"].includes(width)}
                    companyRole={companyRole}
                    comments={comments}
                    addComment={addComment}
                  />
                )}
                {stepDocuments.map(({ element, documents: matched }, index) => {
                  const { StepComponent } = getRegistryEntry(element.type);
                  return (
                    <StepComponent
                      key={`${effectiveStepDocumentType(element)}:${index}`}
                      stepConfig={element}
                      documents={matched}
                      userInfo={userInfo}
                      requestId={id!}
                      companyRole={companyRole}
                      canEdit={canEdit}
                      isMobile={["xs", "sm"].includes(width)}
                      comments={comments}
                      addComment={addComment}
                      statusesConfig={statusesConfig}
                      onRefreshDocuments={onRefresh}
                      expandedDocumentType={expandedDocumentType}
                      identificationProvider={config?.identification_provider}
                    />
                  );
                })}
                {orphanDocuments.map((doc) => (
                  <DocumentTypeRenderer
                    key={doc.document.documentId || doc.document.documentType}
                    document={doc}
                    requestId={id!}
                    title={titleCase(doc.document.documentType)}
                    companyRole={companyRole}
                    canEdit={canEdit}
                    comments={comments}
                    addComment={addComment}
                    userInfo={userInfo}
                    isMobile={["xs", "sm"].includes(width)}
                    statusesConfig={statusesConfig}
                    onRefreshDocuments={onRefresh}
                    defaultExpanded={
                      expandedDocumentType === doc.document.documentType
                    }
                  />
                ))}
                <AddDocumentControl
                  candidates={addableCandidates}
                  onAddDocument={handleAddDocument}
                />
              </>
            ) : (
              <>
                <ChecksPanel
                  verificationRequestId={id ?? ""}
                  externalIds={externalIds}
                  screeningEvents={screeningEvents}
                  refreshScreeningEvents={refreshScreeningEvents}
                  isMobile={["xs", "sm"].includes(width)}
                  onRefresh={onRefresh}
                />
                <HistoryPanel
                  statusesConfig={statusesConfig}
                  history={history}
                  verificationRequestId={id ?? ""}
                  onLoadMore={onLoadMoreHistory}
                />
              </>
            )}
          </LeftColumn>
          {!["xs"].includes(width) ? (
            <RightColumn>
              <ActionPanel
                statusesConfig={statusesConfig}
                actions={actions}
                companyRole={companyRole}
                statusReason={data?.statusReason}
                loading={loading}
                status={data?.status}
                onAction={onAction}
                riskLevel={
                  data?.riskLevel
                    ? {
                        riskLevel: data.riskLevel,
                        riskLevelReason: data?.riskLevelReason,
                      }
                    : null
                }
              />
              {isIndividual ? (
                <IdentificationPanel data={data} documents={documents} />
              ) : null}
              <DatabaseScreeningPanel
                data={data}
                onRefresh={onRefresh}
                onExternalIdsRefresh={refreshExternalIds}
              />
              <DuplicatesPanel />
              <StellarPanel data={data} />
              <LinkedVRsPanel verificationRequestId={id} statusesConfig={statusesConfig} />
            </RightColumn>
          ) : null}
        </Stack>
      </Box>
    </>
  );
};
